import {
  getGetBackExportTemplateTypeQueryKey,
  useGetBackExportTemplateType,
  usePostBackExportTemplate,
} from '@framework/api-client/api/backoffice/export-template-controller/export-template-controller.ts';
import { type ExportTemplateDTO } from '@framework/api-client/api/backoffice/schemas/exportTemplateDTO.ts';
import { type ExportTemplateDTOType } from '@framework/api-client/api/backoffice/schemas/exportTemplateDTOType.ts';
import { useCallback, useMemo, useState } from 'react';
import { useQueryClient } from 'react-query';
import { TemplateSaveActionEnum } from '../ExportFileTypes.ts';
import { type IExportTemplateCardProps } from './ExportTemplateCard.tsx';
import { type ExportTemplateSelectProps } from './ExportTemplateSelect.tsx';

interface UseExportTemplateParams {
  templateType?: ExportTemplateDTOType;
}

export interface ExportTemplateConfig {
  saveTemplate: (selected: string[]) => Promise<void>;
  isSaving: boolean;
  templateSelectProps: Omit<ExportTemplateSelectProps, 'onSelect'>;
  templateCardProps: IExportTemplateCardProps;
}

/**
 * Hook che gestisce l'intero ciclo di vita dei template di esportazione:
 * fetch, selezione, salvataggio (nuovo/aggiorna) e invalidazione cache.
 *
 * Lo stato UI (template selezionato, azione di salvataggio, nome nuovo template)
 * è gestito internamente. Il consumer riceve solo:
 * - `shouldShowTemplates` per decidere se renderizzare la UI template
 * - `saveTemplate(fields)` da invocare al momento dell'export
 * - `isSaving` per disabilitare i pulsanti durante il salvataggio
 * - `selectProps` / `cardProps` da passare direttamente ai sotto-componenti
 * - `onSelectFields` callback per popolare i campi selezionati dal template
 */
export function useExportTemplate(
  params: UseExportTemplateParams,
): ExportTemplateConfig {
  const { templateType } = params;
  const queryClient = useQueryClient();

  const { data, isLoading: isTemplateLoading } = useGetBackExportTemplateType(
    templateType ?? 'EXPORT_PROCEDIMENTI_CSV',
    { query: { enabled: templateType != null } },
  );

  const postMutation = usePostBackExportTemplate();

  const templates: ExportTemplateDTO[] | undefined = data;

  const templateOptions = useMemo(
    () =>
      templates?.map(item => ({
        label: item.name,
        value: item.id,
      })) ?? [],
    [templates],
  );

  const [templateSaveAction, setTemplateSaveAction] = useState(
    TemplateSaveActionEnum.NO_SAVE,
  );
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [newTemplateName, setNewTemplateName] = useState<string | undefined>(
    undefined,
  );

  const resetState = useCallback(() => {
    setTemplateSaveAction(TemplateSaveActionEnum.NO_SAVE);
    setNewTemplateName(undefined);
    setSelectedTemplate('');
  }, []);

  const saveTemplate = useCallback(
    async (selected: string[]) => {
      if (templateSaveAction === TemplateSaveActionEnum.NO_SAVE) return;

      if (
        templateSaveAction === TemplateSaveActionEnum.NEW_TEMPLATE &&
        (!newTemplateName || newTemplateName === '')
      ) {
        throw new Error('Il nome del nuovo template è obbligatorio');
      }

      await postMutation.mutateAsync({
        data: {
          name:
            newTemplateName ??
            templates?.find(item => item.id === selectedTemplate)?.name ??
            '',
          template: JSON.stringify(selected),
          type: templateType!,
          id:
            templateSaveAction === TemplateSaveActionEnum.UPDATE_TEMPLATE
              ? selectedTemplate
              : undefined,
        },
      });
      await queryClient.invalidateQueries(
        getGetBackExportTemplateTypeQueryKey(templateType!),
      );

      resetState();
    },
    [
      postMutation,
      templateSaveAction,
      newTemplateName,
      selectedTemplate,
      templates,
      templateType,
      queryClient,
      resetState,
    ],
  );

  const templateSelectProps: Omit<ExportTemplateSelectProps, 'onSelect'> =
    useMemo(
      () => ({
        options: templateOptions,
        isLoading: isTemplateLoading,
        setSelectedTemplate,
        data: templates,
        selectedTemplate,
        templateType,
      }),
      [
        templateOptions,
        isTemplateLoading,
        selectedTemplate,
        templates,
        templateType,
      ],
    );

  const templateCardProps: IExportTemplateCardProps = useMemo(
    () => ({
      selectedTemplate,
      templateSaveAction,
      setTemplateSaveAction,
      setNewTemplateName,
    }),
    [selectedTemplate, templateSaveAction],
  );

  return {
    saveTemplate,
    isSaving: postMutation.isLoading,
    templateSelectProps,
    templateCardProps,
  };
}
